import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon, ShieldCheckIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionLabel } from '../components/SectionLabel';
import { CERTIFICATION, CONTACT_EMAIL, CONTACT_PHONES, OFFICES, PRODUCTS } from '../data/site';
type FormState = {
  name: string;
  email: string;
  organization: string;
  interest: string;
  message: string;
};
type Errors = Partial<Record<keyof FormState, string>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';
const EMPTY: FormState = {
  name: '',
  email: '',
  organization: '',
  interest: PRODUCTS[0].name,
  message: ''
};
export function Contact() {
  const [values, setValues] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  function update(field: keyof FormState, value: string) {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
    setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  }
  function validate(): Errors {
    const next: Errors = {};
    if (!values.name.trim()) next.name = 'Please tell us your name.';
    if (!values.email.trim()) {
      next.email = 'We need an email to reply to.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      next.email = 'That email address does not look right.';
    }
    if (!values.organization.trim()) next.organization = 'Which organization are you with?';
    if (values.message.trim().length < 20) next.message = 'A sentence or two about the system helps us route this.';
    return next;
  }
  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    await new Promise(resolve => setTimeout(resolve, 900));
    setStatus('success');
    setValues(EMPTY);
  }
  const fieldClass = 'w-full border border-ink-300 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 transition-colors duration-150 ease-out focus:border-bronze-500 focus:outline-none';
  return <>
      <PageHero eyebrow="Contact" title="Book a demo, or join an early access list." lede={`Tell us what you manage — a property portfolio, a land registry, or a SACCO — and we will show you the product that fits. Or email us directly at ${CONTACT_EMAIL}.`} data-id="element-241" data-mp="1r66qraplrei6" />

      <section className="border-b border-ink-100" data-id="element-242" data-mp="1d772w59wz3n2">
        <div className="mx-auto grid max-w-container gap-12 px-6 py-16 lg:grid-cols-[1.25fr_0.75fr] lg:gap-20 lg:px-10 lg:py-24" data-id="element-243" data-mp="1d801uk2msoun">
          <div data-id="element-244" data-mp="kqe52atnvst8">
            <SectionLabel data-id="element-245" data-mp="kpumqt03n2eo">Send an enquiry</SectionLabel>

            <AnimatePresence mode="wait" initial={false} data-id="element-246" data-mp="ko9sl4hp5lhc">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.25,
              ease: [0.23, 1, 0.32, 1]
            }} className="mt-8 border border-bronze-200 bg-bronze-50 p-8" role="status" data-id="element-247" data-mp="fp700jiwl7q9">
                  <CheckCircle2Icon className="h-6 w-6 text-bronze-600" aria-hidden="true" data-id="element-248" data-mp="2md7cg7jnfq8i" />
                  <h2 className="mt-4 text-xl font-semibold tracking-tight text-ink-900" data-id="element-249" data-mp="3f365sf4vm9iz">
                    Received. You will hear from us within one business day.
                  </h2>
                  <p className="mt-3 max-w-lg text-sm leading-relaxed text-ink-600" data-id="element-250" data-mp="3559yjyf27yq5">
                    If you are an existing PropFlow customer with an urgent
                    issue, message the support line on WhatsApp instead of
                    waiting on email.
                  </p>
                  <button type="button" onClick={() => setStatus('idle')} className="mt-6 border border-ink-300 px-5 py-2.5 text-sm font-medium text-ink-800 transition-colors duration-150 ease-out hover:border-ink-900" data-id="element-251" data-mp="29yan5xf9lqwy">
                    Send another enquiry
                  </button>
                </motion.div> : <motion.form key="form" initial={false} onSubmit={handleSubmit} noValidate className="mt-8 space-y-6" data-id="element-252" data-mp="2svu03c4ufton">
                  <div className="grid gap-6 sm:grid-cols-2" data-id="element-253" data-mp="2iww3dlx9ktuk">
                    <Field id="name" label="Name" error={errors.name} value={values.name} onChange={v => update('name', v)} className={fieldClass} placeholder="Jordan Ellis" autoComplete="name" data-id="element-254" data-mp="2exvpmhtn8hef" />
                    <Field id="email" label="Work email" type="email" error={errors.email} value={values.email} onChange={v => update('email', v)} className={fieldClass} placeholder="jordan@company.com" autoComplete="email" data-id="element-255" data-mp="3j646voytnfv5" />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2" data-id="element-256" data-mp="ar2k0fej1s1q">
                    <Field id="organization" label="Organization" error={errors.organization} value={values.organization} onChange={v => update('organization', v)} className={fieldClass} placeholder="Agency, SACCO, or institution" autoComplete="organization" data-id="element-257" data-mp="arlvbd9o9i6s" />
                    <div data-id="element-258" data-mp="3ftwb40djjpy2">
                      <label htmlFor="interest" className="block text-sm font-medium text-ink-800" data-id="element-259" data-mp="3fuo2ez03v1ij">
                        Area of interest
                      </label>
                      <select id="interest" value={values.interest} onChange={e => update('interest', e.target.value)} className={`${fieldClass} mt-2`} data-id="element-260" data-mp="3owdrvzaffqdo">
                        {PRODUCTS.map(product => <option key={product.slug} value={product.name} data-id="element-261" data-mp="cce0xmaaq6uh">
                            {product.name} — {product.category}
                          </option>)}
                        <option value="Partnership or investment" data-id="element-262" data-mp="le3r6osfyzqo">
                          Partnership or investment
                        </option>
                        <option value="Not sure yet" data-id="element-263" data-mp="14x19qf40hq9i">Not sure yet</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-264" data-mp="v1py45nwgz5z">
                    <label htmlFor="message" className="block text-sm font-medium text-ink-800" data-id="element-265" data-mp="v0eq1tnf9592">
                      What do you manage, and what is going wrong today?
                    </label>
                    <textarea id="message" rows={6} value={values.message} onChange={e => update('message', e.target.value)} className={`${fieldClass} mt-2 resize-y`} placeholder="62 units across three properties in Kampala. Rent chasing is manual and maintenance requests keep getting lost in WhatsApp…" aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} data-id="element-266" data-mp="qzus4nv0xxlv" />
                    {errors.message && <p id="message-error" className="mt-2 flex items-center gap-1.5 text-sm text-bronze-700" data-id="element-267" data-mp="ipp41c3zbaf6">
                        <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-268" data-mp="8t2j6nbnkjd5" />
                        {errors.message}
                      </p>}
                  </div>

                  {status === 'error' && <p role="alert" className="flex items-center gap-2 border border-bronze-200 bg-bronze-50 px-4 py-3 text-sm text-bronze-800" data-id="element-269" data-mp="3r7la69vjwd0f">
                      <AlertCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-270" data-mp="3h95s0wei4e32" />
                      Some details are missing — check the highlighted fields above.
                    </p>}

                  <div className="flex flex-wrap items-center gap-4" data-id="element-271" data-mp="1sqjdox30p5bn">
                    <button type="submit" disabled={status === 'submitting'} className="inline-flex items-center gap-2 bg-ink-900 px-7 py-3.5 text-sm font-medium text-white transition-colors duration-150 ease-out hover:bg-bronze-600 disabled:cursor-not-allowed disabled:bg-ink-400" data-id="element-272" data-mp="1wrd2dsoaao5l">
                      {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-273" data-mp="1e03tnlmc83f2" />}
                      {status === 'submitting' ? 'Sending…' : 'Send enquiry'}
                    </button>
                    <p className="text-xs text-ink-500" data-id="element-274" data-mp="qbsviq810huh">
                      We never share enquiry details outside Keystone.
                    </p>
                  </div>
                </motion.form>}
            </AnimatePresence>
          </div>

          <aside className="space-y-10" data-id="element-275" data-mp="ozyg3h1fnv21">
            <div data-id="element-276" data-mp="tvvxzrsch9gx">
              <h2 className="text-xs font-medium uppercase tracking-[0.18em] text-ink-500" data-id="element-277" data-mp="tvdr81823cur">
                Demos & enquiries
              </h2>
              <a href={`mailto:${CONTACT_EMAIL}`} className="mt-5 block break-words text-lg font-semibold tracking-tight text-ink-900 underline decoration-bronze-500 underline-offset-[6px] transition-colors duration-150 ease-out hover:text-bronze-600" data-id="element-278" data-mp="13uuw4gfhhmpo">
                {CONTACT_EMAIL}
              </a>
              <p className="mt-3 text-sm leading-relaxed text-ink-600" data-id="element-279" data-mp="g4ihyntnatqn">
                For demo requests, partnerships, and general questions. We reply
                within one business day.
              </p>
              <h3 className="mt-8 text-xs font-medium uppercase tracking-[0.18em] text-ink-500" data-id="element-280" data-mp="3jlhevyi6qcmj">
                Call us
              </h3>
              <ul className="mt-3 space-y-2" data-id="element-281" data-mp="25pzeoqaach99">
                {CONTACT_PHONES.map(phone => <li key={phone.tel} data-id="element-282" data-mp="21oe1f3tua0yj">
                    <a href={`tel:${phone.tel}`} className="whitespace-nowrap text-lg font-semibold tracking-tight text-ink-900 transition-colors duration-150 ease-out hover:text-bronze-600" data-id="element-283" data-mp="21pfc376y0lfq">
                      {phone.display}
                    </a>
                  </li>)}
              </ul>
            </div>

            <div className="flex items-start gap-3 border-l-2 border-bronze-500 pl-4" data-id="element-284" data-mp="1hzc05m5v6tdq">
              <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-bronze-600" aria-hidden="true" data-id="element-285" data-mp="1dy09ref6244h" />
              <p className="text-sm leading-relaxed text-ink-700" data-id="element-286" data-mp="18xm05rxx2m4c">
                <span className="font-semibold text-ink-900" data-id="element-287" data-mp="13zapxg3pm9ll">
                  {CERTIFICATION.label}.
                </span>{' '}
                Your enquiry details are handled under the same standard as the
                data in our products.
              </p>
            </div>

            <div data-id="element-288" data-mp="ud9pmn7ubjki">
              <h2 className="text-xs font-medium uppercase tracking-[0.18em] text-ink-500" data-id="element-289" data-mp="udt2hpimvhro">
                Where we are
              </h2>
              <ul className="mt-5 space-y-4 text-sm leading-relaxed" data-id="element-290" data-mp="gfl3zd5y88ee">
                {OFFICES.map(office => <li key={office.city} className="border-t border-ink-100 pt-4" data-id="element-291" data-mp="184oehaofiezh">
                    <span className="block font-medium text-ink-900" data-id="element-292" data-mp="1d3hr70vw61yl">
                      {office.city}
                    </span>
                    <span className="block text-ink-600" data-id="element-293" data-mp="zcw4f13bh7vf">{office.line1}</span>
                    <span className="block text-ink-600" data-id="element-294" data-mp="13dg1y5rsydi3">{office.line2}</span>
                  </li>)}
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>;
}
type FieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  className: string;
  placeholder?: string;
  type?: string;
  error?: string;
  autoComplete?: string;
};
function Field({
  id,
  label,
  value,
  onChange,
  className,
  placeholder,
  type = 'text',
  error,
  autoComplete
}: FieldProps) {
  return <div data-id="element-295" data-mp="3qcaablmtdd9h">
      <label htmlFor={id} className="block text-sm font-medium text-ink-800" data-id="element-296" data-mp="3qc1xftqoy7ya">
        {label}
      </label>
      <input id={id} type={type} value={value} placeholder={placeholder} autoComplete={autoComplete} onChange={e => onChange(e.target.value)} className={`${className} mt-2`} aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined} data-id="element-297" data-mp="2xljviw1pecmg" />
      {error && <p id={`${id}-error`} className="mt-2 flex items-center gap-1.5 text-sm text-bronze-700" data-id="element-298" data-mp="32j1vhwxqthib">
          <AlertCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-299" data-mp="mvbmd0g954pq" />
          {error}
        </p>}
    </div>;
}